import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { ethers } from 'ethers';
import MercurXSpinner from '../../components/MercurXSpinner/MercurXSpinner';
import { abiRequestAction } from '../../store/abi/abiActions';
import { getTokens } from '../../store/token/tokenActions';
import wallet from '../../helpers/wallet';
import {
  getNftCollectionContractAddress,
  isAddress,
  resolveNftCollectionConfig,
} from '../../helpers/nftSale';
import './MyTickets.scss';

const formatCount = (value) => {
  return Number(value || 0).toLocaleString('tr-TR');
};

const truncateMiddle = (value, startLength = 8, endLength = 6) => {
  const normalizedValue = `${value ?? ''}`.trim();

  if (!normalizedValue) {
    return '-';
  }

  if (normalizedValue.length <= startLength + endLength + 3) {
    return normalizedValue;
  }

  return `${normalizedValue.slice(0, startLength)}...${normalizedValue.slice(-endLength)}`;
};

const toSafeNumber = (value) => {
  const parsedValue = Number(value?.toString?.() ?? value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getBscScanAddressUrl = (address) => {
  return `https://testnet.bscscan.com/address/${address}`;
};

const safeReadContractMethod = async (contract, methodName, args = []) => {
  if (typeof contract?.[methodName] !== 'function') {
    return null;
  }

  try {
    return await contract[methodName](...args);
  } catch (error) {
    return null;
  }
};

function MyTickets({ ...props }) {
  const {
    accounts,
    abiHistory,
    abiHistoryRequest,
    nftProjects,
    getTokensRequest,
  } = props;
  const [ownedTickets, setOwnedTickets] = useState([]);
  const [isPortfolioLoading, setIsPortfolioLoading] = useState(true);
  const [portfolioError, setPortfolioError] = useState('');

  const accountAddress = accounts?.[0] || '';
  const normalizedAccountAddress = accountAddress ? `${accountAddress}`.trim() : '';
  const totalCollections = ownedTickets.length;
  const totalTickets = ownedTickets.reduce((sum, ticket) => sum + (ticket.quantity || 0), 0);
  const hasWalletConnection = Boolean(normalizedAccountAddress);
  const isBootstrapLoading = nftProjects === null || abiHistory === null;

  const portfolioSummary = useMemo(() => {
    return [
      {
        label: 'Owned Collections',
        value: formatCount(totalCollections),
        note: 'Distinct NFT sale projects in your wallet',
      },
      {
        label: 'Total Tickets',
        value: formatCount(totalTickets),
        note: 'All non-transferable tickets currently held',
      },
      {
        label: 'Connected Wallet',
        value: normalizedAccountAddress
          ? truncateMiddle(normalizedAccountAddress, 10, 6)
          : 'Wallet not connected',
        note: normalizedAccountAddress || 'Connect your wallet to inspect ticket ownership',
      },
    ];
  }, [normalizedAccountAddress, totalCollections, totalTickets]);

  useEffect(() => {
    abiHistoryRequest();
    getTokensRequest({ filter: 'nft' });
  }, [abiHistoryRequest, getTokensRequest]);

  useEffect(() => {
    let isCancelled = false;

    const loadOwnedTickets = async () => {
      if (isBootstrapLoading) {
        return;
      }

      if (!hasWalletConnection || typeof window.ethereum === 'undefined') {
        setOwnedTickets([]);
        setPortfolioError('');
        setIsPortfolioLoading(false);
        return;
      }

      setIsPortfolioLoading(true);
      setPortfolioError('');

      try {
        await wallet.controlAndSwitchOrAddNetwork();
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const checksumAccountAddress = ethers.utils.getAddress(normalizedAccountAddress);
        const ownedTicketEntries = [];

        for (const project of nftProjects || []) {
          const collectionConfig = resolveNftCollectionConfig(project, abiHistory);
          const collectionAddress = collectionConfig.collectionContractAddress ||
            getNftCollectionContractAddress(project);

          if (!collectionConfig.contractAbi || !isAddress(collectionAddress)) {
            continue;
          }

          const contract = new ethers.Contract(
            collectionAddress,
            collectionConfig.contractAbi,
            provider,
          );
          const balanceResult = await safeReadContractMethod(contract, 'balanceOf', [
            checksumAccountAddress,
          ]);
          const quantity = toSafeNumber(balanceResult);

          if (quantity <= 0) {
            continue;
          }

          const totalMinted = toSafeNumber(await safeReadContractMethod(contract, 'totalMinted'));
          const maxSupply = toSafeNumber(await safeReadContractMethod(contract, 'maxSupply'));

          ownedTicketEntries.push({
            project,
            quantity,
            collectionAddress,
            totalMinted,
            maxSupply,
          });
        }

        if (!isCancelled) {
          setOwnedTickets(ownedTicketEntries);
        }
      } catch (error) {
        if (!isCancelled) {
          setOwnedTickets([]);
          setPortfolioError(error?.message || 'Ticket portfolio could not be loaded.');
        }
      } finally {
        if (!isCancelled) {
          setIsPortfolioLoading(false);
        }
      }
    };

    loadOwnedTickets();

    return () => {
      isCancelled = true;
    };
  }, [
    abiHistory,
    hasWalletConnection,
    isBootstrapLoading,
    nftProjects,
    normalizedAccountAddress,
  ]);

  return (
    <Container className="my-tickets-page mt-4 mt-lg-5 px-3 px-lg-5">
      <Card className="my-tickets-hero-card">
        <div className="my-tickets-hero-glow"></div>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <Badge className="my-tickets-hero-badge">Wallet Portfolio</Badge>
          <span className="text-fs-body-sm text-light">Non-transferable NFT tickets</span>
        </div>

        <Row className="g-4 align-items-end">
          <Col lg={7}>
            <h1 className="text-fs-head-lg text-light mb-2">My Tickets</h1>
            <p className="text-fs-body-md text-t-body-color-light mb-0">
              Review every NFT ticket currently held by your connected wallet, inspect collection
              details, and jump back into the related sale projects when you want to mint more.
            </p>
          </Col>
          <Col lg={5}>
            <div className="my-tickets-wallet-panel">
              <div className="my-tickets-wallet-label">Active wallet</div>
              <div className="my-tickets-wallet-value">
                {normalizedAccountAddress
                  ? truncateMiddle(normalizedAccountAddress, 12, 8)
                  : 'Wallet not connected'}
              </div>
              <div className="my-tickets-wallet-note">
                {normalizedAccountAddress || 'Connect MetaMask to read ticket ownership on-chain.'}
              </div>
            </div>
          </Col>
        </Row>

        <div className="my-tickets-stats-grid">
          {portfolioSummary.map((stat) => {
            return (
              <div className="my-tickets-stat-card" key={stat.label}>
                <div className="my-tickets-stat-label">{stat.label}</div>
                <div className="my-tickets-stat-value">{stat.value}</div>
                <div className="my-tickets-stat-note">{stat.note}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {portfolioError && (
        <Card className="my-tickets-feedback-card my-tickets-feedback-card--error">
          <div className="text-fs-head-xs text-light mb-1">Portfolio load issue</div>
          <div className="text-fs-body-md text-t-body-color-light">{portfolioError}</div>
        </Card>
      )}

      {(isBootstrapLoading || isPortfolioLoading) && (
        <Card className="my-tickets-feedback-card">
          <MercurXSpinner />
        </Card>
      )}

      {!isBootstrapLoading && !isPortfolioLoading && !hasWalletConnection && (
        <Card className="my-tickets-empty-card">
          <div className="my-tickets-empty-badge">Wallet Required</div>
          <h2 className="text-fs-head-md text-light mt-2 mb-2">Connect your wallet</h2>
          <p className="text-fs-body-md text-t-body-color-light mb-4">
            Ticket ownership is read directly from your connected wallet on BSC Testnet.
          </p>
          <div className="d-flex flex-wrap gap-3">
            <Button className="my-tickets-primary-button" onClick={wallet.connectWallet}>
              Connect Wallet
            </Button>
            <Button
              as={Link}
              to="/sales/nft"
              variant="outline-light"
              className="my-tickets-secondary-button"
            >
              Browse NFT Sales
            </Button>
          </div>
        </Card>
      )}

      {!isBootstrapLoading &&
        !isPortfolioLoading &&
        hasWalletConnection &&
        !ownedTickets.length && (
          <Card className="my-tickets-empty-card">
            <div className="my-tickets-empty-badge">No Tickets Yet</div>
            <h2 className="text-fs-head-md text-light mt-2 mb-2">
              This wallet does not hold any NFT tickets
            </h2>
            <p className="text-fs-body-md text-t-body-color-light mb-4">
              Explore the active NFT sale projects and mint your first ticket from the launchpad.
            </p>
            <Button
              as={Link}
              to="/sales/nft"
              className="my-tickets-primary-button"
            >
              Go To NFT Sales
            </Button>
          </Card>
        )}

      {!isBootstrapLoading && !isPortfolioLoading && ownedTickets.length > 0 && (
        <div className="my-tickets-grid">
          {ownedTickets.map((ticket) => {
            const { project } = ticket;

            return (
              <Card className="my-ticket-card" key={project?.id}>
                <div className="my-ticket-card__glow"></div>
                <div className="my-ticket-card__artwork-wrap">
                  <img
                    src={`${process.env.REACT_APP_API_URL}/projects/${project?.id}/image`}
                    alt={project?.nft?.name || project?.name}
                    className="my-ticket-card__artwork"
                  />
                </div>
                <div className="my-ticket-card__body">
                  <div
                    className="d-flex justify-content-between align-items-center gap-2 flex-wrap"
                  >
                    <Badge className="my-ticket-card__badge">
                      {project?.nft?.symbol || 'NFT'}
                    </Badge>
                    <div className="my-ticket-card__quantity">{ticket.quantity} owned</div>
                  </div>

                  <h2 className="text-fs-head-sm text-light mt-3 mb-2">
                    {project?.nft?.name || project?.name}
                  </h2>
                  <p className="text-fs-body-md text-t-body-color-light mb-3">
                    {project?.explanation_text ||
                      'Your wallet currently holds one or more launchpad tickets for this NFT ' +
                        'collection.'}
                  </p>

                  <div className="my-ticket-card__metrics">
                    <div className="my-ticket-card__metric">
                      <span className="my-ticket-card__metric-label">Collection</span>
                      <span className="my-ticket-card__metric-value">
                        {truncateMiddle(ticket.collectionAddress)}
                      </span>
                    </div>
                    <div className="my-ticket-card__metric">
                      <span className="my-ticket-card__metric-label">Sale Status</span>
                      <span className="my-ticket-card__metric-value">
                        {project?.is_active === 'active'
                          ? 'Active'
                          : project?.is_active === 'completed'
                            ? 'Completed'
                            : 'Upcoming'}
                      </span>
                    </div>
                    <div className="my-ticket-card__metric">
                      <span className="my-ticket-card__metric-label">Ticket Price</span>
                      <span className="my-ticket-card__metric-value">
                        {formatCount(project?.nft?.price_in_mercurx)} MERX
                      </span>
                    </div>
                    <div className="my-ticket-card__metric">
                      <span className="my-ticket-card__metric-label">Supply</span>
                      <span className="my-ticket-card__metric-value">
                        {ticket.totalMinted && ticket.maxSupply
                          ? `${formatCount(ticket.totalMinted)} / ${formatCount(ticket.maxSupply)}`
                          : formatCount(project?.nft?.total_supply)}
                      </span>
                    </div>
                  </div>

                  <div className="my-ticket-card__footer">
                    <a
                      href={getBscScanAddressUrl(ticket.collectionAddress)}
                      target="_blank"
                      rel="noreferrer"
                      className="my-ticket-card__link"
                    >
                      View Contract
                    </a>
                    <Button
                      as={Link}
                      to={`/sales/nft/project/${project?.id}`}
                      className="my-ticket-card__button"
                    >
                      Open Project
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Container>
  );
}

const mapStateToProps = (state) => {
  return {
    accounts: state.walletReducer.accounts,
    abiHistory: state.abiReducer.abiHistory,
    nftProjects: state.tokenReducer.tokens,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    abiHistoryRequest: (payload) => {
      dispatch(abiRequestAction(payload));
    },
    getTokensRequest: (payload) => {
      dispatch(getTokens(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MyTickets);
